import { z } from 'zod';
import { MACRO_VISUAL_PROFILE, type MacroVisualProfile } from '../macroProfile';

/** Hamming channel: bit counts, denominator 1, four-bit nuisance margin. */
export const VISUAL_HAMMING_DENOMINATOR = 1;
export const VISUAL_HAMMING_GUARD = 4;
/** Spatial channel: `spatialNumerator` over `2*765*128*128`, guard `denominator/1024`. */
export const VISUAL_SPATIAL_DENOMINATOR = 25_067_520;
export const VISUAL_SPATIAL_GUARD = 24_480;
/** Source-detail residual shares spatial units, but has an independently learned envelope. */
export const VISUAL_DETAIL_DENOMINATOR = VISUAL_SPATIAL_DENOMINATOR;
export const VISUAL_DETAIL_GUARD = VISUAL_SPATIAL_GUARD;
/** Exact common units for max(hamming / 256, spatial / spatialMaximum). */
export const VISUAL_HAMMING_RAW_SCALE = VISUAL_SPATIAL_DENOMINATOR / 256;
/** Calibration evidence, not a cap on user-edited runtime thresholds. */
export const VISUAL_CALIBRATION_RADIUS_MS = 100;
/** Retired streaming-fit cutoff. It no longer drops benign rows. */
export const VISUAL_CALIBRATION_MAX_DIFFERENCE_PERCENT = 10;
/**
 * Synthetic benign rows above this fused percent are not members of the envelope.
 * Must match AnalysisCalibrationAccumulator.cpp.
 */
export const VISUAL_CALIBRATION_BENIGN_CEILING_PERCENT = 20;
/**
 * Captured frames at or above this fused percent are outliers and do not set the envelope.
 * Must match AnalysisCalibrationAccumulator.cpp.
 */
export const VISUAL_CALIBRATION_CAPTURED_OUTLIER_PERCENT = 30;
// Legacy policy fallback: without a per-node threshold only an exact match is accepted.
export const VISUAL_ACCEPT_NUMERATOR = 0;
export const VISUAL_ACCEPT_DENOMINATOR = 1;
export const VISUAL_MARGIN_NUMERATOR = 10001;
export const VISUAL_MARGIN_DENOMINATOR = 1_000_000;
export const VISUAL_COMPARISON_LIMIT = 1_048_576;

export const macroVisualDigestSchema = z.string().regex(/^[0-9a-f]{64}$/);
export const visualAnalysisErrorCodeSchema = z.enum([
  'duplicate_sample_identity', 'missing_training_evidence', 'spatial_evidence_inseparable',
  'hash_evidence_inseparable', 'geometry_incompatible', 'comparison_limit_exceeded',
  'invalid_normalization', 'invalid_raw_evidence', 'missing_validation_evidence',
  'degenerate_live_hash', 'incomplete_evidence', 'incompatible_profile',
  'missing_artifact', 'stale_revision', 'lease_expired', 'device_lost',
  'coverage_incomplete', 'conflicting_chunk',
  'analysis_gpu_unavailable', 'analysis_adapter_identity_unavailable', 'analysis_adapter_type_unavailable',
  'analysis_hardware_gpu_required', 'analysis_driver_identity_unavailable',
  'analysis_selected_host_offline', 'analysis_selected_host_incompatible',
  'analysis_selected_host_busy', 'analysis_selected_host_unavailable',
  'analysis_host_acquisition_timeout', 'analysis_execution_timeout', 'analysis_stale_projection',
  'analysis_manifest_schedule_mismatch', 'analysis_asset_grant_failed', 'analysis_asset_access_revoked',
  'analysis_input_scope_mismatch', 'analysis_lease_expiring', 'analysis_output_scope_mismatch',
  'analysis_resume_chunk_mismatch', 'analysis_write_identity_required', 'invalid_analysis_asset_token',
  'invalid_analysis_grant_lifetime', 'analysis_native_reply_invalid', 'local_graph_asset_budget_exceeded',
]);
export type VisualAnalysisErrorCode = z.infer<typeof visualAnalysisErrorCodeSchema>;
export const macroVisualProfileSchema = z.literal(MACRO_VISUAL_PROFILE);
export const macroVisualPresetSchema = z.literal('macro-nuisance-v1');

export const pixelRegionSchema = z.object({
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
  width: z.number().int().positive().max(16_384),
  height: z.number().int().positive().max(16_384),
}).strict();

export const rawVisualScoreSchema = z.object({
  hamming: z.number().int().min(0).max(256),
  referencePopcount: z.number().int().min(0).max(256),
  candidatePopcount: z.number().int().min(0).max(256),
  spatialNumerator: z.number().int().nonnegative().max(VISUAL_SPATIAL_DENOMINATOR),
  /** Absent only in historical evidence that predates source-detail descriptors. */
  detailNumerator: z.number().int().nonnegative().max(VISUAL_DETAIL_DENOMINATOR).optional(),
  /** Spacing-two source edges, calibrated independently of adjacent-pixel detail. */
  frequencyNumerator: z.number().int().nonnegative().max(VISUAL_DETAIL_DENOMINATOR).optional(),
  /** A source direction lost activity beyond the declared two-capture noise bound. */
  detailStructureLost: z.boolean().optional(),
}).strict();

const channelUnit = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const normalizerSchema = z.object({
  median: channelUnit,
  benignResidualUpper: channelUnit,
  negativeResidualAnchor: channelUnit,
  guard: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  trainingDigest: macroVisualDigestSchema,
}).strict().superRefine((value, context) => {
  if (value.negativeResidualAnchor <= value.benignResidualUpper) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['negativeResidualAnchor'], message: 'Normalizer range is empty' });
  }
});

/**
 * How much benign training evidence the ceiling discarded. `poor` is true when a captured frame
 * (identity or temporal neighbour) was excluded, or when at least a quarter of the benign rows were.
 * Must match AnalysisCalibrationAccumulator.cpp.
 */
export const trainingQualitySchema = z.object({
  benignRows: z.number().int().nonnegative().max(VISUAL_COMPARISON_LIMIT),
  excludedRows: z.number().int().nonnegative().max(VISUAL_COMPARISON_LIMIT),
  excludedCapturedRows: z.number().int().nonnegative().max(VISUAL_COMPARISON_LIMIT),
  poor: z.boolean(),
}).strict().superRefine((value, context) => {
  if (value.excludedRows > value.benignRows || value.excludedCapturedRows > value.excludedRows) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['excludedRows'], message: 'Excluded rows exceed benign rows' });
  }
});

const readyNormalizationFields = {
  readiness: z.literal('ready'),
  /** Automatic decision boundary. Envelope scores use separately learned channel tolerances. */
  recommendedThresholdPercent: z.number().int().min(0).max(100).optional(),
  /** Absent on legacy artifacts, which did not apply the benign ceiling. */
  trainingQuality: trainingQualitySchema.optional(),
  spatial: normalizerSchema,
  detail: normalizerSchema.optional(),
  frequency: normalizerSchema.optional(),
  /** Absent on legacy artifacts, which calibrated the channels before fusion. */
  combined: z.object({ method: z.enum(['raw-max-v1', 'channel-envelope-v1', 'channel-envelope-v2', 'channel-envelope-v3']), normalizer: normalizerSchema }).strict().optional(),
};
export const readyNormalizationSchema = z.discriminatedUnion('hashUsable', [
  z.object({ ...readyNormalizationFields, hashUsable: z.literal(true), hamming: normalizerSchema }).strict(),
  z.object({ ...readyNormalizationFields, hashUsable: z.literal(false), hamming: z.null() }).strict(),
]);

export const failedNormalizationSchema = z.object({
  readiness: z.enum(['invalid', 'inseparable']),
  errorCode: visualAnalysisErrorCodeSchema,
  spatial: z.null(),
  hamming: z.null(),
  hashUsable: z.literal(false),
}).strict();

export const calibrationOutcomeSchema = z.union([readyNormalizationSchema, failedNormalizationSchema]);
export const sampleLabelSchema = z.enum(['benign', 'material', 'stress', 'noop']);
export const sampleSplitSchema = z.enum(['training', 'validation', 'diagnostic']);
const sampleCaseId = z.string().min(1).max(128);
const sampleReferenceId = z.string().min(1).max(128);
const sampleCandidateId = z.string().min(1).max(128);
const sampleTransformId = z.string().min(1).max(128);
/** Scored evidence, or a kept abstention that carries `errorCode` and no `raw`. */
export const scoredCalibrationSampleSchema = z.object({
  caseId: sampleCaseId,
  referenceId: sampleReferenceId,
  candidateId: sampleCandidateId,
  transformId: sampleTransformId,
  label: sampleLabelSchema,
  split: sampleSplitSchema,
  raw: rawVisualScoreSchema,
}).strict();
export const abstainedCalibrationSampleSchema = z.object({
  caseId: sampleCaseId,
  referenceId: sampleReferenceId,
  candidateId: sampleCandidateId,
  transformId: sampleTransformId,
  label: sampleLabelSchema,
  split: sampleSplitSchema,
  errorCode: visualAnalysisErrorCodeSchema,
}).strict();
export const calibrationSampleSchema = z.union([scoredCalibrationSampleSchema, abstainedCalibrationSampleSchema]);

export const normalizedVisualScoreSchema = z.union([
  z.object({
    status: z.literal('scored'),
    hamming: z.number().min(0).max(1).nullable(),
    spatial: z.number().min(0).max(1),
    detail: z.number().min(0).max(1).optional(),
    frequency: z.number().min(0).max(1).optional(),
    difference: z.number().min(0).max(1),
    differenceNumerator: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    differenceDenominator: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    /** Envelope display scale. Competition compares difference / (this percent / 100). Absent for raw/legacy scores. */
    calibrationThresholdPercent: z.number().int().min(5).max(99).optional(),
    /** Valid comparison outside a required structural or exact-only constraint; wider thresholds cannot accept it. */
    forcedMismatch: z.enum(['structure_lost', 'outside_zero_envelope']).optional(),
    product: z.number().min(0).max(1).nullable(),
    productNumerator: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    productDenominator: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    noisyOr: z.number().min(0).max(1).nullable(),
    noisyOrNumerator: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    noisyOrDenominator: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  }).strict().superRefine((value, context) => {
    const pairs: [number, number, string][] = [
      [value.differenceNumerator, value.differenceDenominator, 'differenceNumerator'],
      [value.productNumerator, value.productDenominator, 'productNumerator'],
      [value.noisyOrNumerator, value.noisyOrDenominator, 'noisyOrNumerator'],
    ];
    for (const [numerator, denominator, path] of pairs) {
      if (numerator > denominator) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: 'Scored ratio exceeds one' });
      }
    }
  }),
  z.object({ status: z.literal('abstain'), errorCode: visualAnalysisErrorCodeSchema }).strict(),
]);

export type PixelRegion = z.infer<typeof pixelRegionSchema>;
export type RawVisualScore = z.infer<typeof rawVisualScoreSchema>;
export type Normalizer = z.infer<typeof normalizerSchema>;
export type ReadyNormalization = z.infer<typeof readyNormalizationSchema>;
export type TrainingQuality = z.infer<typeof trainingQualitySchema>;
export type CalibrationOutcome = z.infer<typeof calibrationOutcomeSchema>;
export type ScoredCalibrationSample = z.infer<typeof scoredCalibrationSampleSchema>;
export type AbstainedCalibrationSample = z.infer<typeof abstainedCalibrationSampleSchema>;
export type CalibrationSample = ScoredCalibrationSample | AbstainedCalibrationSample;
export type NormalizedVisualScore = z.infer<typeof normalizedVisualScoreSchema>;

export interface RgbSource {
  readonly width: number;
  readonly height: number;
  readonly rgb: Uint8Array;
}
export interface VisualDescriptor {
  readonly profile: MacroVisualProfile;
  readonly hash: Uint8Array;
  readonly popcount: number;
  readonly rgb64: Uint8Array;
  readonly rgb128: Uint8Array;
  /** Version 1: RGB tile-major 16x16 directional edge energies; gx Q8 low half, gy Q8 high half. */
  readonly detail?: Uint32Array;
  /** Version 1: identical packing to detail, with source endpoints two pixels apart. */
  readonly frequency?: Uint32Array;
  /** Source-pixel geometry for bounded small-region alignment; not part of the packed descriptor. */
  readonly regionWidth?: number;
  readonly regionHeight?: number;
}
